package br.com.htms.inspeccaocampo.ui.dashboards

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.ui.kit.CartaoSecao
import br.com.htms.inspeccaocampo.ui.kit.CoresDash
import br.com.htms.inspeccaocampo.ui.kit.FaixaDash
import br.com.htms.inspeccaocampo.ui.kit.FatiaDash
import br.com.htms.inspeccaocampo.ui.kit.PortalButton
import br.com.htms.inspeccaocampo.ui.kit.SerieDash
import br.com.htms.inspeccaocampo.ui.kit.contarPorFaixa
import br.com.htms.inspeccaocampo.ui.theme.PortalCores

internal fun <T> contagem(itens: List<T>, chave: (T) -> String): List<Pair<String, Double>> =
    itens.groupingBy(chave).eachCount().map { it.key to it.value.toDouble() }.sortedByDescending { it.second }

internal fun <T> soma(itens: List<T>, chave: (T) -> String, valor: (T) -> Double): List<Pair<String, Double>> =
    itens.groupBy(chave).map { (k, v) -> k to v.sumOf(valor) }.sortedByDescending { it.second }

/** Cor fixa por posição no ranking, para a rosca e as colunas usarem as mesmas cores. */
internal fun coresRanking(chaves: List<String>): Map<String, Color> =
    chaves.mapIndexed { i, k -> k to CoresDash.de(k, CoresDash.corLong(i)) }.toMap()

internal fun fatias(itens: List<Pair<String, Double>>, cores: Map<String, Color>, limite: Int = 8): List<FatiaDash> {
    val principais = itens.take(limite).map { (k, v) -> FatiaDash(k, v, cores[k] ?: CoresDash.neutro) }
    val resto = itens.drop(limite).sumOf { it.second }
    return if (resto > 0) principais + FatiaDash("Outros", resto, CoresDash.neutro) else principais
}

/** Séries empilhadas por chave (as [top] maiores + "Outros"). */
internal fun <T> seriesPorChave(
    itens: List<T>,
    faixas: List<FaixaDash>,
    data: (T) -> String,
    chave: (T) -> String,
    cores: Map<String, Color>,
    top: Int = 5
): List<SerieDash> {
    val principais = contagem(itens, chave).take(top).map { it.first }
    val series = principais.map { k -> SerieDash(k, cores[k] ?: CoresDash.neutro, contarPorFaixa(itens.filter { chave(it) == k }, faixas, data)) }
    val outros = itens.filter { chave(it) !in principais }
    return if (outros.isEmpty()) series else series + SerieDash("Outros", CoresDash.neutro, contarPorFaixa(outros, faixas, data))
}

internal fun alternar(atual: String?, novo: String): String? = if (atual == novo) null else novo

/** Carrega os dados de um dashboard fora da tela do módulo (usado no módulo Dashboards). */
@Composable
internal fun <T> CarregadorDash(
    titulo: String,
    chave: Any = Unit,
    carregar: suspend () -> Result<T>,
    conteudo: @Composable (dados: T, recarregar: () -> Unit, carregando: Boolean) -> Unit
) {
    var dados by remember(chave) { mutableStateOf<T?>(null) }
    var erro by remember(chave) { mutableStateOf<String?>(null) }
    var carregando by remember(chave) { mutableStateOf(true) }
    var tick by remember(chave) { mutableStateOf(0) }
    LaunchedEffect(chave, tick) {
        carregando = true
        carregar()
            .onSuccess { dados = it; erro = null }
            .onFailure { erro = it.message ?: "Falha ao carregar os dados." }
        carregando = false
    }
    val d = dados
    if (d != null) {
        conteudo(d, { tick++ }, carregando)
    } else if (erro != null) {
        CartaoSecao(titulo, subtitulo = erro.orEmpty()) {
            PortalButton(onClick = { tick++ }) { Text("Tentar novamente") }
        }
    } else {
        CartaoSecao(titulo, subtitulo = "Carregando dados...") {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                CircularProgressIndicator(color = PortalCores.Navy)
            }
        }
    }
}
